// 🌿 API Craft Back Office - API Service
import axios, { AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse, 
  User, 
  ApiUserProfile,
  DbModel, 
  DynamicRecord, 
  CreateRecordRequest, 
  UpdateRecordRequest 
} from '../types';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 🔐 Auth API
export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    console.log('🔄 API: Sending login request to:', `${API_BASE_URL}/auth/login`);
    console.log('🔄 API: Request payload:', { email: credentials.email, password: '***' });
    
    try {
      const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
      console.log('✅ API: Login response received:', { 
        status: response.status, 
        hasToken: !!response.data.access_token,
        hasUser: !!response.data.user 
      });
      return response.data;
    } catch (error) {
      console.error('🚨 API: Login request failed:', error);
      throw error;
    }
  },

  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  getProfile: async (): Promise<ApiUserProfile> => {
    const response: AxiosResponse<ApiUserProfile> = await api.get('/auth/profile');
    return response.data;
  },

  refreshToken: async (refreshToken: string): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },
};

// 📊 Models API
export const modelsApi = {
  getUserModels: async (): Promise<DbModel[]> => {
    console.log('🔄 API: Fetching user models from /generic/');
    const response = await api.get('/generic/');
    console.log('✅ API: Raw response structure:', {
      hasData: !!response.data,
      hasModels: !!(response.data && response.data.models),
      modelsCount: response.data?.models?.length || 0,
      firstModel: response.data?.models?.[0]
    });
    
    // The backend returns GetUserModelsResponse structure
    if (response.data && response.data.models) {
      const mappedModels = response.data.models.map((model: any) => ({
        id: model.model_name, // Use model_name as id for now
        user_id: response.data.user_id,
        model: model.model_name,
        table_name: model.table_name,
        table_structure: model.table_structure || { fields: [] }, // Now includes structure
        created_at: model.created_at,
        updated_at: model.created_at
      }));
      
      console.log('🔄 API: Mapped models:', mappedModels);
      return mappedModels;
    }
    
    return [];
  },

  createModel: async (modelDefinition: any): Promise<void> => {
    console.log('🔄 API: Creating model with definition:', modelDefinition);
    const response = await api.post('/generator/generate', modelDefinition);
    console.log('✅ API: Model created successfully:', response.data);
    return response.data;
  },

  deleteModel: async (modelId: string): Promise<void> => {
    await api.delete(`/models/${modelId}`);
  },
};

// 🤖 AI Assistant API
export const aiApi = {
  chat: async (message: string): Promise<any> => {
    const response = await api.post('/ai-assistant/query', {
      message: message
    });
    return response.data;
  }
};

// 📝 Dynamic CRUD API
export const crudApi = {
  getRecords: async (
    modelName: string,
    page: number = 1,
    pageSize: number = 10,
    expand?: string
  ): Promise<ApiResponse<DynamicRecord[]>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      select_fields: '*',
    });

    if (expand) {
      params.append('expand', expand);
    }

    const response: AxiosResponse<ApiResponse<DynamicRecord[]>> = await api.get(
      `/generic/${modelName}?${params.toString()}`
    );
    return response.data;
  },

  getRecord: async (
    modelName: string,
    id: string,
    expand?: string
  ): Promise<DynamicRecord> => {
    const params = new URLSearchParams({
      select_fields: '*',
    });

    if (expand) {
      params.append('expand', expand);
    }

    const response: AxiosResponse<DynamicRecord> = await api.get(
      `/generic/${modelName}/${id}?${params.toString()}`
    );
    return response.data;
  },

  createRecord: async (
    modelName: string,
    data: CreateRecordRequest
  ): Promise<DynamicRecord> => {
    const response: AxiosResponse<DynamicRecord> = await api.post(
      `/generic/${modelName}`,
      data
    );
    return response.data;
  },

  updateRecord: async (
    modelName: string,
    id: string,
    data: UpdateRecordRequest
  ): Promise<DynamicRecord> => {
    console.log(`🔄 API: Updating ${modelName} record`, { id, data });
    try {
      const response: AxiosResponse<DynamicRecord> = await api.put(
        `/generic/${modelName}/${id}`,
        data
      );
      console.log('✅ API: Update successful', response.data);
      return response.data;
    } catch (error) {
      console.error('🚨 API: Update failed', error);
      throw error;
    }
  },

  deleteRecord: async (modelName: string, id: string): Promise<void> => {
    await api.delete(`/generic/${modelName}/${id}`);
  },
};

// 📈 Analytics API (for future use)
export const analyticsApi = {
  getModelStats: async (modelName: string): Promise<any> => {
    const response = await api.get(`/analytics/models/${modelName}/stats`);
    return response.data;
  },

  getUsageStats: async (): Promise<any> => {
    const response = await api.get('/analytics/usage');
    return response.data;
  },
};

// 🛠️ Utility functions
export const apiUtils = {
  isOnline: async (): Promise<boolean> => {
    try {
      await api.get('/health', { timeout: 3000 });
      return true;
    } catch {
      return false;
    }
  },

  getApiInfo: async (): Promise<any> => {
    const response = await api.get('/info');
    return response.data;
  },
};

export default api;
